import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {useSlotRecipe, Box} from '@chakra-ui/react3'
import navigationTeaserRecipe from './index.recipe'
import {hasValidImageSrc, hasValidString} from '../../../utils/utils'
import {ArrowRightIcon} from '../../icons'
import RichText from '../../cms/rich-text'
import DisImage from '../../../components/dis-image'
import useCtaAction from '../../../hooks/useCtaAction'
import {isExternalLink} from '../../../../utils/utils'
import {normalizeContentLink} from '../../../../utils/url'
import {trackNavigationContentTileClick} from '../../../../tracking/analytics'

/**
 * Whether a CMS teaser entry carries anything visible to render.
 *
 * A teaser is optional at every level, but the single `mainTeaser` at levels 2
 * and 3 is emitted by the CMS template unconditionally: an unauthored one
 * arrives as a full object of empty strings rather than absent, so a caller
 * cannot tell it apart from an authored one by truthiness and renders an empty
 * card (STOK-17335).
 *
 * Image, tag and title are the only fields that put something on screen, and any
 * one of them is enough — an image-only teaser and a text-only one are both
 * authored. A `link` is not consulted: on its own it makes the empty card
 * clickable, not visible. The image crops checked are exactly the three the
 * component resolves from, so the level-3 subtree's misspelled `imagePortait` is
 * deliberately left out — the component cannot read it either (see the known
 * contract gaps in `docs/agents/navigation-tree.md`).
 *
 * Every caller gates on this **before** rendering the teaser, and this component
 * deliberately does not gate itself: `NavigationHide` wraps each teaser in a box
 * of its own, so a teaser that returned nothing would still leave a child behind
 * and the `_empty` rule that collapses the surrounding column would never match.
 *
 * @param {Object} [teaser] - A CMS teaser entry.
 * @returns {boolean}
 */
export const hasNavigationTeaserContent = (teaser) =>
    hasValidImageSrc(teaser?.imageSquare) ||
    hasValidImageSrc(teaser?.imagePortrait) ||
    hasValidImageSrc(teaser?.imageLandscape) ||
    hasValidString(teaser?.tag) ||
    hasValidString(teaser?.title)

const NavigationTeaser = React.forwardRef(
    ({variant, imagePortrait, imageLandscape, imageSquare, tag, title, link}, _ref) => {
        const recipe = useSlotRecipe({recipe: navigationTeaserRecipe})

        const {baseImage, sImage} = useMemo(() => {
            const emptyImage = {}
            const portrait = hasValidImageSrc(imagePortrait) ? imagePortrait : null
            const landscape = hasValidImageSrc(imageLandscape) ? imageLandscape : null
            const square = hasValidImageSrc(imageSquare) ? imageSquare : null

            // Crop fallback chains per spec:
            // Main teaser (large) mobile: Landscape -> Square -> Portrait
            const largeMobile = landscape || square || portrait || emptyImage
            // Main teaser (large) desktop: Portrait -> Square -> Landscape
            const largeDesktop = portrait || square || landscape || emptyImage

            // Small teaser (Level 1, 2, 3): Square -> Landscape -> Portrait
            const smallResolved = square || landscape || portrait || emptyImage

            return variant === 'small' || variant === 'landscape'
                ? {baseImage: smallResolved, sImage: smallResolved}
                : {baseImage: largeMobile, sImage: largeDesktop}
        }, [variant, imagePortrait, imageLandscape, imageSquare])

        const hasCta = Boolean(link?.type)
        const styles = useMemo(() => recipe({variant, hasCta}), [recipe, variant, hasCta])
        const ctaProps = useCtaAction({
            action: link,
            openInNewTab: isExternalLink(normalizeContentLink(link?.value)),
            onClick: () => trackNavigationContentTileClick(title)
        })
        const wrapperProps = hasCta ? ctaProps : {as: 'div'}

        return (
            <Box
                {...wrapperProps}
                className="group leap-navigation-teaser-wrapper"
                css={styles.wrapper}
                data-testid="navigation-teaser"
            >
                <DisImage
                    src={baseImage.src}
                    alt={baseImage.alt}
                    title={baseImage.title}
                    sources={[
                        {src: baseImage.disBaseLink || baseImage.src, breakpoint: 'base'},
                        {src: sImage.disBaseLink || sImage.src, breakpoint: 's'}
                    ]}
                    // the teaser slot is reused across panes, so a new URL here is a different image (STOK-17559)
                    resetOnSrcChange
                    {...styles.image}
                />
                <Box css={styles.bottomContainer}>
                    <Box css={styles.textContainer}>
                        <RichText css={styles.tag}>{tag}</RichText>
                        <RichText css={styles.title}>{title}</RichText>
                    </Box>
                    {hasCta && (
                        <Box css={styles.arrowContainer}>
                            <Box css={styles.arrowIconCurrent}>
                                <ArrowRightIcon />
                            </Box>
                            <Box css={styles.arrowIconIncoming}>
                                <ArrowRightIcon />
                            </Box>
                        </Box>
                    )}
                </Box>
                {(!!tag || !!title) && <Box css={styles.darkenLayerBottom}></Box>}
            </Box>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    NavigationTeaser.displayName = 'NavigationTeaser'
}

NavigationTeaser.propTypes = {
    variant: PropTypes.oneOf(Object.keys(navigationTeaserRecipe.variants.variant)),
    imagePortrait: PropTypes.object,
    imageLandscape: PropTypes.object,
    imageSquare: PropTypes.object,
    tag: PropTypes.string,
    title: PropTypes.string,
    link: PropTypes.object
}

export default NavigationTeaser
